/**
 * Verifier Layout
 * 
 * Dedicated layout for verifier-focused users with simplified navigation
 * focused on verification tasks.
 */

import { Outlet, NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardCheck,
  ShieldCheck,
  Settings,
  Key,
  Menu,
  X,
  ChevronRight,
  LogOut,
  Home,
  FileCode2,
  Clock,
  Activity,
} from "lucide-react";
import { useState } from "react";
import { useAuthStore, PLAN_LIMITS } from "../stores/auth";
import { Footer } from "./Footer";

const verifierNavigation = [
  { name: "Dashboard", href: "/verifier", icon: LayoutDashboard },
  { name: "Create Request", href: "/verify/request", icon: ClipboardCheck },
  { name: "Verify Proof", href: "/verify", icon: ShieldCheck },
  { name: "Request History", href: "/verifier/requests", icon: Clock },
  { name: "Integration", href: "/verifier/integration", icon: FileCode2 },
  { name: "API Keys", href: "/api-keys", icon: Key },
  { name: "Settings", href: "/settings", icon: Settings },
];

export function VerifierLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant, tenantName, logout } = useAuthStore();

  // Get breadcrumb from current path
  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === "/verifier") return "Verifier Dashboard";
    if (path === "/verify/request") return "Create Request";
    if (path === "/verifier/requests") return "Request History";
    if (path === "/verifier/integration") return "Integration Guide";
    const nav = verifierNavigation.find((n) => n.href === path);
    return nav?.name || "Page";
  };

  // Calculate usage percentage
  const getUsageInfo = () => {
    const plan = tenant?.plan || "starter";
    const limits = PLAN_LIMITS[plan as keyof typeof PLAN_LIMITS];
    const used = tenant?.usage?.verifications || 0;
    const max = limits.verifications;
    const percentage = max === Infinity ? 0 : Math.min((used / max) * 100, 100);
    
    return {
      used,
      max: max === Infinity ? "∞" : max.toLocaleString(),
      percentage,
      plan: plan.charAt(0).toUpperCase() + plan.slice(1),
    };
  };

  const usageInfo = getUsageInfo();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const switchToIssuerMode = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:relative lg:flex lg:flex-col
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-semibold text-slate-900">ZKCN</span>
              <span className="text-xs text-success-600 ml-1.5 px-1.5 py-0.5 bg-success-50 rounded">Verifier</span>
            </div>
          </div>
          <button
            className="lg:hidden p-1 rounded-md hover:bg-slate-100"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {verifierNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors
                  ${
                    isActive
                      ? "bg-success-50 text-success-700"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }
                `}
              >
                <item.icon
                  className={`w-5 h-5 ${isActive ? "text-success-500" : "text-slate-400"}`}
                />
                {item.name}
              </NavLink>
            );
          })}
        </nav>

        {/* Mode Switcher */}
        <div className="px-4 py-3 border-t border-slate-200">
          <button
            onClick={switchToIssuerMode}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Activity className="w-5 h-5 text-slate-400" />
            Switch to Issuer Mode
          </button>
        </div>

        {/* Usage Stats */}
        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2">
            {usageInfo.plan} Plan • Verifications
          </div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-slate-700">{usageInfo.used.toLocaleString()}</span>
            <span className="text-slate-400">/ {usageInfo.max}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-success-400 to-success-500 rounded-full transition-all"
              style={{ width: `${usageInfo.percentage}%` }}
            />
          </div>
        </div>

        {/* User Menu */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-success-100 flex items-center justify-center">
              <span className="text-sm font-medium text-success-700">
                {tenantName?.charAt(0).toUpperCase() || "V"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {tenantName || "Verifier"}
              </p>
              <p className="text-xs text-slate-500 truncate">{usageInfo.plan} Plan</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8 gap-4">
          <button
            className="lg:hidden p-2 -ml-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <Menu className="w-5 h-5 text-slate-500" />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center text-sm">
            <Link
              to="/"
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4 mx-2 text-slate-300" />
            <span className="text-slate-600 font-medium">{getBreadcrumb()}</span>
          </div>

          {/* Right side actions */}
          <div className="ml-auto flex items-center gap-3">
            <Link
              to="/verify/request"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-success-500 text-white rounded-lg text-sm font-medium hover:bg-success-600 transition-colors"
            >
              <ClipboardCheck className="w-4 h-4" />
              New Request
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}
